import { useCallback, useEffect, useMemo, useState } from 'react'
import SummaryDashboard from '../../components/snoop/SummaryDashboard'
import MappingAddForm from '../../components/snoop/MappingAddForm'
import {
  createManualRelease,
  downloadCommitmentsCsv,
  fetchCommitmentReleases,
  fetchCommitments,
  fetchDistroArtists,
  updateCommitment,
  updateReleaseOverride,
} from '../../api/operationsClient'
import { buildDistroSimplifyStatuses } from '../../utils/snoop/distroCommitmentSummary'
import {
  PLACEHOLDER_OPS_COMMITMENTS,
  PLACEHOLDER_OPS_COMMITMENTS_MAPPING,
  PLACEHOLDER_OPS_COMMITMENTS_SIMPLIFY,
} from '../../utils/snoop/placeholderColumns'

const VIEW_OPTIONS = [
  { value: 'commitments', label: 'Commitments' },
  { value: 'mapping', label: 'Mapping' },
]

/** Attribution status → short label, matching upstream. */
const ATTR_LABELS = {
  counting: 'Counting',
  matched_no_contract: 'No contract start',
  matched_no_date: 'Undated',
  matched_pre_contract: 'Pre-contract',
  name_candidate: 'Name candidate',
  excluded: 'Excluded',
  force_included: 'Force included',
  manual: 'Manual',
}

/** Statuses produced by auto-matching, i.e. no explicit ops override yet. */
const AUTO_MATCH_STATUSES = new Set([
  'counting',
  'matched_no_contract',
  'matched_no_date',
  'matched_pre_contract',
])

const CLEAR_VALUE = '__clear__'

const btnClass =
  'inline-flex shrink-0 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60'

export default function OpsCommitmentsPage() {
  const [view, setView] = useState('commitments')
  const [includeHidden, setIncludeHidden] = useState(false)
  const [simplify, setSimplify] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [artistOptions, setArtistOptions] = useState([])
  const [exportBusy, setExportBusy] = useState(false)
  const [exportError, setExportError] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const isMapping = view === 'mapping'

  // Artist picker for manual releases; loaded once the add form is opened.
  useEffect(() => {
    if (!showAdd || artistOptions.length) return
    let cancelled = false
    fetchDistroArtists({ includeHidden: false })
      .then((data) => {
        if (cancelled) return
        const rows = Array.isArray(data?.rows) ? data.rows : []
        setArtistOptions(
          rows
            .map((r) => ({ value: String(r.id), label: String(r.artist || '') }))
            .filter((o) => o.value && o.label),
        )
      })
      .catch(() => {
        if (!cancelled) setArtistOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [showAdd, artistOptions.length])

  const fetchData = useCallback(async () => {
    const data = isMapping
      ? await fetchCommitmentReleases({ includeHidden })
      : await fetchCommitments({ includeHidden })

    // Simplify collapses the commitment columns to two derived statuses.
    if (!isMapping && simplify) {
      const rows = (data.rows || []).map((row) => ({
        Artist: row.Artist,
        ...buildDistroSimplifyStatuses(row),
        'Commitment Urgency': row['Commitment Urgency'],
        id: row.id,
      }))
      return {
        ...data,
        rows,
        columns: [
          'Artist',
          'Commitment Status',
          'License/Match Status',
          'Commitment Urgency',
          'id',
        ],
      }
    }
    return data
    // refreshKey is intentional: it forces a refetch after a manual-release add.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapping, includeHidden, simplify, refreshKey])

  /** Commitments: boolean deal flags. The row is created on first edit. */
  const commitmentEditable = useMemo(() => {
    function boolField(key, column, title) {
      return {
        target: 'distro_product_commitment',
        uidColumn: 'id',
        rowIdColumn: 'id',
        control: 'checkbox',
        checkboxTitle: title,
        timestampColumn: 'Updated At',
        getValue: (row) => (String(row?.[column]) === 'Y' ? 'Yes' : ''),
        optimisticPatch: (next) => ({ [column]: next === 'Yes' ? 'Y' : 'N' }),
        onSave: async ({ status, mappingId, uid }) => {
          const id = mappingId ?? uid
          if (id == null || String(id).trim() === '') {
            throw new Error('Missing artist mapping id')
          }
          await updateCommitment(id, { [key]: status === 'Yes' })
        },
      }
    }
    return {
      'Option Period': boolField(
        'option_period', 'Option Period', 'Option period exercised',
      ),
      'License Extend': boolField(
        'license_extend', 'License Extend', 'License extended',
      ),
      Hidden: {
        target: 'distro_product_commitment',
        uidColumn: 'id',
        rowIdColumn: 'id',
        control: 'checkbox',
        checkboxTitle: 'Hide this artist from the default view',
        timestampColumn: 'Updated At',
        optimisticPatch: (next) => ({ Hidden: next === 'Yes' }),
        onSave: async ({ status, mappingId, uid }) => {
          const id = mappingId ?? uid
          if (id == null || String(id).trim() === '') {
            throw new Error('Missing artist mapping id')
          }
          await updateCommitment(id, { hidden: status === 'Yes' })
        },
      },
    }
  }, [])

  /**
   * Mapping: set the song's attribution. Auto-match statuses are display-only
   * (they describe why the matcher counted or skipped a song); choosing
   * Excluded / Force included writes an override, and Clear removes it.
   */
  const mappingEditable = useMemo(
    () => ({
      'Attribution Status': {
        target: 'distro_product_release_override',
        uidColumn: 'Song ID',
        rowIdColumn: 'Song ID',
        allowBlank: false,
        getValue: (row) => String(row?.['Attribution Status'] ?? ''),
        getOptions: (row) => {
          const current = String(row?.['Attribution Status'] ?? '')
          const base = [
            { value: 'excluded', label: ATTR_LABELS.excluded },
            { value: 'force_included', label: ATTR_LABELS.force_included },
          ]
          // Keep the current auto status visible so the closed select reads right.
          if (AUTO_MATCH_STATUSES.has(current)) {
            base.unshift({
              value: current,
              label: ATTR_LABELS[current] || current,
              disabled: true,
            })
          } else {
            base.push({ value: CLEAR_VALUE, label: 'Clear override' })
          }
          return base
        },
        optimisticPatch: (next) => ({
          'Attribution Status': next === CLEAR_VALUE ? 'counting' : next,
        }),
        onSave: async ({ status, row }) => {
          const artistMappingId = row?.['Mapping ID']
          const songId = row?.['Song ID']
          if (!artistMappingId || !songId) {
            throw new Error('Missing Mapping ID / Song ID')
          }
          const action =
            status === CLEAR_VALUE
              ? null
              : status === 'force_included'
                ? 'include'
                : status === 'excluded'
                  ? 'exclude'
                  : null
          await updateReleaseOverride({
            artistMappingId,
            songId,
            action,
            updateAction: true,
          })
        },
      },
    }),
    [],
  )

  async function handleExport() {
    setExportBusy(true)
    setExportError(null)
    try {
      await downloadCommitmentsCsv({ view, includeHidden })
    } catch (err) {
      setExportError(err?.message || 'Export failed')
    } finally {
      setExportBusy(false)
    }
  }

  const defaultSort = useMemo(
    () =>
      isMapping
        ? [
            { column: 'Artist', direction: 'asc' },
            { column: 'Release Date', direction: 'desc' },
          ]
        : [
            { column: 'Commitment Proximity Days', direction: 'asc' },
            { column: 'Artist', direction: 'asc' },
          ],
    [isMapping],
  )

  const headerLabels = useMemo(
    () => ({
      'Luminate Artist ID': 'Luminate ID',
      'Tracks Committed': 'Committed',
      'Tracks Released': 'Released',
      'Tracks Remaining': 'Remaining',
      'Time Commitment (Months)': 'Term (mo)',
      'Tracks Tail (Months)': 'Tail (mo)',
      'Match Length (Months)': 'Match (mo)',
      'License Length (Years)': 'License (yr)',
    }),
    [],
  )

  const hiddenColumns = useMemo(() => {
    if (isMapping) return ['Mapping ID', 'Manual ID', 'Catalog Release Date']
    if (simplify) return ['id']
    return ['id', 'Today', 'Commitment Proximity Days']
  }, [isMapping, simplify])

  const addForm = showAdd ? (
    <MappingAddForm
      title="Add manual release"
      submitLabel="Add"
      fields={[
        {
          name: 'artist_mapping_id',
          label: 'Artist',
          type: 'select',
          options: artistOptions,
          required: true,
        },
        { name: 'title', label: 'Title', required: true },
        { name: 'release_date', label: 'Release Date', placeholder: 'YYYY-MM-DD', required: true },
        { name: 'notes', label: 'Notes' },
      ]}
      onSubmit={async (values) => {
        await createManualRelease(values)
        setRefreshKey((k) => k + 1)
      }}
    />
  ) : null

  const searchActions = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex shrink-0 rounded-lg border border-gray-300 bg-white p-0.5 shadow-sm">
        {VIEW_OPTIONS.map((opt) => {
          const on = view === opt.value
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setView(opt.value)}
              className={[
                'rounded-md px-2.5 py-1.5 text-sm font-medium',
                on ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50',
              ].join(' ')}
              title={
                opt.value === 'commitments'
                  ? 'Deal terms and progress per artist'
                  : 'Which songs count toward each commitment'
              }
            >
              {opt.label}
            </button>
          )
        })}
      </div>
      {!isMapping ? (
        <label
          className="inline-flex items-center gap-2 text-sm text-gray-700"
          title="Collapse to commitment and license/match status"
        >
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300"
            checked={simplify}
            onChange={(e) => setSimplify(e.target.checked)}
          />
          Simplify
        </label>
      ) : null}
      <label
        className="inline-flex items-center gap-2 text-sm text-gray-700"
        title="Hidden artists are omitted by default"
      >
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300"
          checked={includeHidden}
          onChange={(e) => setIncludeHidden(e.target.checked)}
        />
        Show hidden
      </label>
    </div>
  )

  const extraActions = (
    <>
      <button
        type="button"
        className={btnClass}
        onClick={() => setShowAdd((on) => !on)}
        aria-pressed={showAdd}
      >
        {showAdd ? 'Close add' : 'Add manual release'}
      </button>
      <button
        type="button"
        className={btnClass}
        disabled={exportBusy}
        onClick={handleExport}
        title="Download the current view as CSV"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3v12m0 0 4-4m-4 4-4-4M5 19h14"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {exportBusy ? 'Exporting…' : 'Export'}
      </button>
    </>
  )

  const placeholderColumns = isMapping
    ? PLACEHOLDER_OPS_COMMITMENTS_MAPPING
    : simplify
      ? PLACEHOLDER_OPS_COMMITMENTS_SIMPLIFY
      : PLACEHOLDER_OPS_COMMITMENTS

  return (
    <div className="space-y-4">
      {exportError ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          Export failed: {exportError}
        </div>
      ) : null}
      <SummaryDashboard
        title={isMapping ? 'Commitments · Mapping' : 'Commitments'}
        subtitle={
          isMapping
            ? 'Which songs count toward each commitment — exclude or force-include'
            : 'Distro product + time commitments, with tracks released against each deal'
        }
        fetchData={fetchData}
        defaultSort={defaultSort}
        searchPlaceholder={
          isMapping ? 'Search artist, title or song id…' : 'Search artist or Luminate ID…'
        }
        editableStatus={isMapping ? mappingEditable : commitmentEditable}
        searchActions={searchActions}
        extraActions={extraActions}
        addForm={addForm}
        layout="mapping"
        headerLabels={isMapping ? undefined : headerLabels}
        truncateColumns={isMapping ? ['Title', 'Song Display Artist', 'Match Path'] : ['Artist']}
        hiddenColumns={hiddenColumns}
        placeholderColumns={placeholderColumns}
        tableKey={`ops-commitments-${view}-${simplify ? 'simple' : 'full'}-${includeHidden ? 'all' : 'visible'}-${refreshKey}`}
      />
    </div>
  )
}
